<template>
  <div>
    <h4>Appearance</h4>
    <sme-card large>
      <div class="card-container">
        <div class="card-design-uploads">
          <template v-if="showBanner">
            <div class="image-upload-container">
              <div class="label">
                Banner image
                <small>
                  The background image(s) shown for this award in the mobile app. Up to {{ maxBannerImages }}
                  images.
                </small>
              </div>

              <div class="image-upload-actions">
                <b-button variant="primary" v-b-modal.banner-upload-modal size="sm" :disabled="bannerImagesAtLimit">
                  Upload images
                </b-button>
                <span v-b-tooltip.hover="bannerGalleryTooltip" tabindex="0">
                  <b-button
                    variant="outline-primary"
                    v-b-modal.banner-gallery-modal
                    size="sm"
                    :disabled="!hasBannerGalleryImages || bannerImagesAtLimit"
                    >Choose from gallery</b-button
                  >
                </span>
              </div>
              <small v-if="bannerImagesError" class="text-danger d-block mt-1">{{ bannerImagesError }}</small>

              <div class="image-upload-preview-container mt-2">
                <div
                  v-if="bannerImages.length"
                  class="banner-thumb-grid"
                  :class="{ 'banner-thumb-grid--open-banner': isOpenBanner }"
                >
                  <div
                    v-for="image in bannerImages"
                    :key="image.id"
                    class="banner-thumb"
                    :class="{ 'banner-thumb--open-banner': isOpenBanner }"
                  >
                    <img :src="image.url" alt="Banner image" class="banner-thumb__image" />
                    <button type="button" class="banner-thumb__remove" @click="$emit('remove:bannerImage', image.id)">
                      <font-awesome-icon :icon="['fas', 'times']" />
                    </button>
                  </div>
                </div>
                <div v-else class="banner-preview">
                  <div class="image-upload-preview-placeholder">
                    <font-awesome-icon :icon="['far', 'image']" size="2xl" />
                  </div>
                </div>
              </div>
            </div>

            <banner-upload-modal
              modal-id="banner-upload-modal"
              :accepted-file-types="allowedImageTypes"
              :max-size-bytes="maxSizeBytes"
              :max-files="bannerSlotsRemaining"
              :open-banner="isOpenBanner"
              @select="$emit('add:pendingBannerFiles', $event)"
            />

            <image-gallery-modal
              modal-id="banner-gallery-modal"
              :image-type="bannerImageType"
              multiple
              :max-selectable="bannerSlotsRemaining"
              @select="$emit('add:bannerImages', $event)"
            />
          </template>

          <div class="image-upload-container">
            <div class="label">
              Award badge
              <small v-if="badgeCollectible">
                The image which represents this award in the portal and as an optional collectible item in the mobile
                app.
              </small>
              <small v-else>The image which represents this award in the portal.</small>
            </div>

            <div class="image-upload-actions">
              <b-button variant="primary" v-b-modal.badge-upload-modal size="sm">
                {{ badgePreviewUrl ? 'Upload another image' : 'Upload image' }}
              </b-button>
              <span v-b-tooltip.hover="!hasBadgeGalleryImages ? 'No gallery images available' : ''" tabindex="0">
                <b-button
                  variant="outline-primary"
                  v-b-modal.badge-gallery-modal
                  size="sm"
                  :disabled="!hasBadgeGalleryImages"
                  >Choose from gallery</b-button
                >
              </span>
              <b-button v-if="badgePreviewUrl" variant="outline-danger" size="sm" @click="$emit('remove:badge')"
                >Remove</b-button
              >
            </div>

            <div class="image-upload-preview-container mt-2">
              <div class="badge-preview">
                <template v-if="badgePreviewUrl">
                  <img :src="badgePreviewUrl" alt="Badge preview" class="image-upload-preview-image" />
                </template>
                <template v-else>
                  <div class="image-upload-preview-placeholder">
                    <font-awesome-icon :icon="['far', 'image']" size="2xl" />
                  </div>
                </template>
              </div>
            </div>
          </div>

          <image-upload-modal
            modal-id="badge-upload-modal"
            title="Add badge image"
            :accepted-file-types="allowedImageTypes"
            :max-size-bytes="maxSizeBytes"
            :file="badgeFile"
            :preview-url="badgePreviewUrl"
            preview-type="badge"
            supported-text="JPG or PNG • up to 10 MB • Recommended 300 × 300 px (1:1)"
            @update:file="$emit('update:badgeFile', $event)"
            @update:previewUrl="$emit('update:badgePreviewUrl', $event)"
          />

          <image-gallery-modal
            modal-id="badge-gallery-modal"
            image-type="logo"
            @select="$emit('gallerySelect:badge', $event)"
          />
        </div>
      </div>
    </sme-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import SmeCard from '@/components/atoms/SmeCard.vue';
import ImageUploadModal from '@/pages/recognition/components/ImageUploadModal.vue';
import BannerUploadModal from '@/pages/recognition/components/BannerUploadModal.vue';
import ImageGalleryModal from '@/pages/recognition/components/ImageGalleryModal.vue';

const hasBannerGalleryImages = ref(false);
const hasBadgeGalleryImages = ref(false);

onMounted(async () => {
  try {
    const data = await ApiClient.getRecognitionGalleryImages();
    hasBannerGalleryImages.value = Object.keys(data?.generic?.[props.bannerImageType] || {}).length > 0;
    hasBadgeGalleryImages.value = Object.keys(data?.generic?.logo || {}).length > 0;
  } catch {
    // leave both false — buttons stay disabled
  }
});

const props = defineProps({
  bannerImages: {
    type: Array,
    default: () => [],
  },
  bannerImagesError: {
    type: String,
    default: '',
  },
  maxBannerImages: {
    type: Number,
    default: 9,
  },
  badgeFile: {
    type: [Object, File],
    default: null,
  },
  badgePreviewUrl: {
    type: String,
    default: '',
  },
  allowedImageTypes: {
    type: Array,
    default: () => ['image/jpeg', 'image/png'],
  },
  maxSizeBytes: {
    type: Number,
    default: 10 * 1024 * 1024, // 10MB
  },
  badgeCollectible: {
    type: Boolean,
    default: true,
  },
  showBanner: {
    type: Boolean,
    default: true,
  },
  // Open awards use a separate gallery/upload pool ('open_banner') from
  // program/milestone awards ('banner'), and crop to a different aspect ratio.
  bannerImageType: {
    type: String,
    default: 'banner',
  },
});

const isOpenBanner = computed(() => props.bannerImageType === 'open_banner');
const bannerSlotsRemaining = computed(() => Math.max(0, props.maxBannerImages - props.bannerImages.length));
const bannerImagesAtLimit = computed(() => bannerSlotsRemaining.value === 0);
const bannerGalleryTooltip = computed(() => {
  if (bannerImagesAtLimit.value) {
    return `You've reached the ${props.maxBannerImages}-image limit`;
  }
  return !hasBannerGalleryImages.value ? 'No gallery images available' : '';
});

defineEmits([
  'add:bannerImages',
  'add:pendingBannerFiles',
  'remove:bannerImage',
  'update:badgeFile',
  'update:badgePreviewUrl',
  'gallerySelect:badge',
  'remove:badge',
]);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';

.banner-thumb-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  width: 100%;

  &--open-banner {
    grid-template-columns: repeat(2, 1fr);
  }
}

.banner-thumb {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--palette-color-core-1-lighten-90);

  // Open-award banners crop to 4:1 in the app, unlike program/milestone banners.
  &--open-banner {
    aspect-ratio: 4 / 1;
  }
}

.banner-thumb__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.banner-thumb__remove {
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: var(--palette-color-base-white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
}
</style>
